/**
 * Network Health Models
 * Centralized type definitions for the POS network-health monitoring system.
 * All state transitions, thresholds, and data shapes are defined here.
 */

// ─── State Enum ──────────────────────────────────────────────────────────────

export enum NetworkState {
  /** All recent API calls are fast. No indication shown to user. */
  NORMAL = 'NORMAL',

  /** 2 of last 5 API calls are slow. Yellow indicator shown. */
  DEGRADED = 'DEGRADED',

  /** 3+ of last 5 API calls are slow. Orange indicator shown. */
  SLOW = 'SLOW',

  /** Active billing request has been waiting beyond critical threshold. */
  CRITICAL = 'CRITICAL',

  /** Browser is offline or consecutive network errors detected. */
  OFFLINE = 'OFFLINE',

  /** Network restored; waiting for confirmation of stable connection. */
  RECOVERING = 'RECOVERING',
}

// ─── Billing Phase Enum ───────────────────────────────────────────────────────

export enum BillingPhase {
  /** No billing request in flight. */
  IDLE = 'IDLE',

  /** Billing request sent, waiting for response (< BILLING_WARN_MS). */
  PROCESSING = 'PROCESSING',

  /** Billing is slow: show warning toast. */
  SLOW_WARN = 'SLOW_WARN',

  /** Billing is critically slow: show critical sticky toast. */
  CRITICAL = 'CRITICAL',
}

// ─── Request Sample ───────────────────────────────────────────────────────────

export interface RequestSample {
  /** Relative URL path of the request (used for diagnostics only). */
  url: string;

  /** Total elapsed time in milliseconds from request start to complete/error. */
  durationMs: number;

  /** True if the request ended with a network-level error (HTTP status 0). */
  isNetworkError: boolean;

  /** True if the request was aborted by the frontend timeout operator. */
  isTimeout: boolean;

  /** Unix timestamp (ms) when the request was initiated. */
  startedAt: number;
}

// ─── Thresholds (single source of truth) ─────────────────────────────────────

export const NETWORK_THRESHOLDS = {
  /** API calls below this are considered healthy. */
  FAST_MS: 800,

  /** API calls above this are considered slow. */
  SLOW_MS: 3000,

  /** Frontend-enforced request timeout (ms). */
  TIMEOUT_MS: 15_000,

  /** How many recent samples to keep in the rolling window. */
  ROLLING_WINDOW_SIZE: 5,

  /** Slow-sample count in the rolling window to enter DEGRADED state. */
  DEGRADED_THRESHOLD: 2,

  /** Slow-sample count in the rolling window to enter SLOW state. */
  SLOW_THRESHOLD: 3,

  /** Consecutive fast samples required to exit SLOW/DEGRADED. */
  RECOVERY_FAST_COUNT: 3,

  /** Debounce (ms) before promoting RECOVERING → NORMAL. */
  RECOVERY_DEBOUNCE_MS: 5_000,

  /** Billing warning shown after this many ms of waiting. */
  BILLING_WARN_MS: 8_000,

  /** Billing critical message shown after this many ms of waiting. */
  BILLING_CRITICAL_MS: 12_000,

  /** Consecutive network errors before forcing OFFLINE state. */
  CONSECUTIVE_ERROR_THRESHOLD: 3,
} as const;

// ─── Network Health Snapshot ──────────────────────────────────────────────────

export interface NetworkHealthSnapshot {
  state: NetworkState;
  isOnline: boolean;
  avgLatencyMs: number;
  consecutiveFastCount: number;
  consecutiveErrorCount: number;
  rollingWindow: RequestSample[];
}
